import { useState, useCallback } from "react"
import { toast } from "@/src/common/snackbar"
import { apiUrls, httpClient } from "@/src/api"
import { getApiErrorInfo } from "@/src/utils/api-error"
import { DashboardContent } from "@/src/layouts/dashboard"

import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Stack from "@mui/material/Stack"
import { LoadingButton } from "@mui/lab"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import InputAdornment from "@mui/material/InputAdornment"

// ----------------------------------------------------------------------

const INDIA_COUNTRY_CODE = "91"
const NATIONAL_MOBILE_DIGIT_COUNT = 10

/**
 * Keeps at most 10 national digits. If the user pastes a full `91…` number, drops the leading `91` first.
 */
function sanitizeNationalTenDigits(raw: string): string {
    let digitsOnly = raw.replace(/\D/g, "")
    if (digitsOnly.startsWith(INDIA_COUNTRY_CODE) && digitsOnly.length > NATIONAL_MOBILE_DIGIT_COUNT) {
        digitsOnly = digitsOnly.slice(INDIA_COUNTRY_CODE.length)
    }
    return digitsOnly.slice(0, NATIONAL_MOBILE_DIGIT_COUNT)
}

/** `phoneNumber` query param: `91` + exactly 10 national digits (e.g. `919820498007`). */
function buildIndiaPhoneNumberForApi(nationalTenDigits: string): string {
    if (nationalTenDigits.length !== NATIONAL_MOBILE_DIGIT_COUNT) return ""
    return `${INDIA_COUNTRY_CODE}${nationalTenDigits}`
}

function readOtpCodeFromLookupResponse(payload: unknown): string | null {
    if (payload === null || payload === undefined || typeof payload !== "object") return null
    const { data } = payload as Record<string, unknown>
    if (data === null || data === undefined || typeof data !== "object") return null
    const { otpCode } = data as Record<string, unknown>
    if (typeof otpCode === "string" && otpCode.trim()) return otpCode.trim()
    if (typeof otpCode === "number" && !Number.isNaN(otpCode)) return String(otpCode)
    return null
}

// ----------------------------------------------------------------------

const GenerateOtp = () => {
    const [phoneNumberInput, setPhoneNumberInput] = useState("")
    const [lookupLoading, setLookupLoading] = useState(false)
    const [resolvedOtp, setResolvedOtp] = useState<string | null>(null)

    const clearFormAndResult = useCallback(() => {
        setPhoneNumberInput("")
        setResolvedOtp(null)
    }, [])

    const requestLookupOtp = useCallback(async () => {
        const phoneNumber = buildIndiaPhoneNumberForApi(phoneNumberInput)
        if (!phoneNumber) {
            toast.error(`Enter all ${NATIONAL_MOBILE_DIGIT_COUNT} digits of the mobile number`)
            return
        }
        setLookupLoading(true)
        setResolvedOtp(null)
        try {
            const response = await httpClient.get(apiUrls.ADMIN_LOOKUP_OTP, {
                params: { phoneNumber },
            })
            setResolvedOtp(readOtpCodeFromLookupResponse(response.data))
        } catch (error) {
            const { message } = getApiErrorInfo(error, "Failed to look up OTP")
            toast.error(message)
        } finally {
            setLookupLoading(false)
        }
    }, [phoneNumberInput])

    return (
        <DashboardContent maxWidth="sm">
            <Typography variant="h4" sx={{ mb: 2, mt: 5 }}>
                Generate / lookup OTP
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter a 10-digit mobile number after +91. The API is called with phoneNumber including the 91 country
                code.
            </Typography>

            <Card sx={{ p: 3 }}>
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        label="Phone number"
                        placeholder="9999999999"
                        value={phoneNumberInput}
                        onChange={(event) => setPhoneNumberInput(sanitizeNationalTenDigits(event.target.value))}
                        disabled={lookupLoading}
                        autoComplete="tel"
                        inputMode="numeric"
                        helperText={`${phoneNumberInput.length}/${NATIONAL_MOBILE_DIGIT_COUNT} digits`}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Typography
                                        component="span"
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ userSelect: "none" }}
                                    >
                                        +{INDIA_COUNTRY_CODE}
                                    </Typography>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <LoadingButton
                            variant="contained"
                            loading={lookupLoading}
                            onClick={() => {
                                requestLookupOtp()
                            }}
                        >
                            Look up OTP
                        </LoadingButton>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={clearFormAndResult}
                            disabled={lookupLoading}
                        >
                            Clear
                        </Button>
                    </Stack>

                    {resolvedOtp !== null && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                OTP
                            </Typography>
                            <Typography
                                variant="h5"
                                component="p"
                                sx={{
                                    fontFamily: "monospace",
                                    letterSpacing: 2,
                                    wordBreak: "break-all",
                                }}
                            >
                                {resolvedOtp}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Card>
        </DashboardContent>
    )
}

export default GenerateOtp
